import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmCommentApi, crmCommentQueryKeys } from "service/crm/api/comment";
import { companyQueryKeys } from "service/crm/api/company"; // adjust path
import { contactQueryKeys } from "service/crm/api/contact"; // adjust path
import { dealQueryKeys } from "service/crm/api/deal"; // adjust path

import { CrmCommentHooks, CrmParentType, CrmEntityType } from "types/crm/comments";
import { usePaginatedList } from "../usePaginatedList";

// Resolves the activity-feed query key for whichever parent entity this
// comment hook set was created for (contact / company / deal).
function getParentActivitiesKey(parentType: CrmParentType, parentUuid: string) {
  switch (parentType) {
    case "companies":
      return companyQueryKeys.activities(parentUuid);
    case "contacts":
      return contactQueryKeys.activities(parentUuid);
    case "deals":
      return dealQueryKeys.activities(parentUuid);
    default:
      return undefined;
  }
}

export function createCrmCommentHooks(
  parentType: CrmParentType,
  entityType: CrmEntityType,
): CrmCommentHooks {
  return {
    useAddComment: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ parentUuid, entityUuid, commentText, parentCommentUuid }: {
          parentUuid: string; entityUuid: string; commentText: string; parentCommentUuid?: string | null;
        }) => crmCommentApi.addComment(parentType, parentUuid, entityType, entityUuid, commentText, parentCommentUuid),
        onSuccess: (_data, variables) => {
          queryClient.invalidateQueries({
            queryKey: crmCommentQueryKeys.base(parentType, variables.parentUuid, entityType, variables.entityUuid),
          });
          const activitiesKey = getParentActivitiesKey(parentType, variables.parentUuid);
          if (activitiesKey) {
            queryClient.invalidateQueries({ queryKey: activitiesKey });
          }
        },
      });
    },

    useUpdateComment: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ parentUuid, entityUuid, commentUuid, commentText, parentCommentUuid }: {
          parentUuid: string; entityUuid: string; commentUuid: string; commentText: string; parentCommentUuid?: string | null;
        }) => crmCommentApi.updateComment(parentType, parentUuid, entityType, entityUuid, commentUuid, commentText, parentCommentUuid),
        onSuccess: (_data, variables) => {
          queryClient.invalidateQueries({
            queryKey: crmCommentQueryKeys.base(parentType, variables.parentUuid, entityType, variables.entityUuid),
          });
          const activitiesKey = getParentActivitiesKey(parentType, variables.parentUuid);
          if (activitiesKey) {
            queryClient.invalidateQueries({ queryKey: activitiesKey });
          }
        },
      });
    },

    useDeleteComment: () => {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ parentUuid, entityUuid, commentUuid }: {
          parentUuid: string; entityUuid: string; commentUuid: string;
        }) => crmCommentApi.deleteComment(parentType, parentUuid, entityType, entityUuid, commentUuid),
        onSuccess: (_data, variables) => {
          queryClient.invalidateQueries({
            queryKey: crmCommentQueryKeys.base(parentType, variables.parentUuid, entityType, variables.entityUuid),
          });
          const activitiesKey = getParentActivitiesKey(parentType, variables.parentUuid);
          if (activitiesKey) {
            queryClient.invalidateQueries({ queryKey: activitiesKey });
          }
        },
      });
    },

    useCommentReplies: (parentUuid, entityUuid, commentUuid, page, enabled) =>
      useQuery({
        queryKey: crmCommentQueryKeys.replies(parentType, parentUuid, entityType, entityUuid, commentUuid, page),
        queryFn: () => crmCommentApi.getCommentReplies(parentType, parentUuid, entityType, entityUuid, commentUuid, page),
        enabled,
      }),

    fetchCommentReplies: (parentUuid, entityUuid, commentUuid, page = 0, size = 20) =>
      crmCommentApi.getCommentReplies(parentType, parentUuid, entityType, entityUuid, commentUuid, page, size),

    useRootComments: (parentUuid, entityUuid, enabled = true) => {
      const { items, isLoading, hasMore, isFetchingMore, onLoadMore, refetch } = usePaginatedList(
        crmCommentQueryKeys.base(parentType, parentUuid, entityType, entityUuid),
        (pageParam: number) => crmCommentApi.getComments(parentType, parentUuid, entityType, entityUuid, pageParam),
        enabled,
      );
      return { comments: items, isLoading, hasMore, isFetchingMore, onLoadMore, refetch };
    },
  };
}